import React from "react";
import { withStyles } from "@material-ui/core/styles";
import DataGrid from '@dataGrid';
import OptimizationPickupsConfig from "./GridConfig";
import { observer, inject } from "mobx-react";
import { Grid } from "@material-ui/core";
import { PickupMap } from "../Map";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import StatusBar from '@dataGrid/components/StatusBar';
import { postData } from "../../../../../api/componentAPIs";
import MESSAGES from "../../../../../appMessages";
import { isPickupFiltered } from "../../../../modules/Dispatch/RouteExecution/filterUtils";
import { getPickupTypes } from "../../../../modules/utils";
import { withFeatureFlags } from "../../../../../userProfile/featureFlagsContext";
import appConstants from "../../../../../appConstants";
import { PICKUP_OPTIMYZER_FILTERS } from "../constants";
import PickupCheckbox from "./PickupCheckbox";
import { RefreshStatus } from "../common";
import {
  isPartialAssignedPickupsSelected,
  isPartialUnassignedPickupsSelected,
  getAllUnassignedPickups,
  getAllAssignedPickups,
  isUnassignedPickupSelected,
  isPickupSelectedInGrid,
  isPendingAssignedPickupSelected,
  getDefaultFilters,
  getDefaultSelectedFilters,
} from "../utils";
import styles from "./styles";

@inject("DispatchStore")
@observer
export class CorrectPickupInfo extends React.Component {
  constructor(props) {
    super(props);
    this.gridApi = {};
    this.state = {
      showPickupsList: props.showPickupsList,
      filters: getDefaultFilters(),
      selectedFilter: getDefaultSelectedFilters(),
      pendingSelectionFromFilter: null,
    };
    this.gridConfig = new OptimizationPickupsConfig({
      userConfig: this.props.userData.userProfile?.columnPref,
      featureFlags: {
        IsWeightBasedRoutingEnabled: this.props.featureFlags.isFeatureEnabled(
          appConstants.features.isWeightBasedRouting
        ),
      },
    });
    this.gridContext = {
      pickupTypeOptions: getPickupTypes(
        this.props.DispatchStore.pickupPriorityShipmentList || []
      ),
    };
  }

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.filterPickupsList(this.syncGridSelectionWithCurrentState);
  };

  componentDidMount() {
    this.props.glEventHub.on(
      "pickupSelectedOnMapPopup",
      this.onPickupSelectedOnMap
    );
    const isAssigned = isPendingAssignedPickupSelected(
      this.props.DispatchStore.pickupListForOptimization,
      this.getSelectedPickupIdList()
    );
    const isUnassigned = this.getDefaultUnassignedValue();
    this.setState({
      selectedFilter: {
        ...this.state.selectedFilter,
        isAssigned,
        isUnassigned: isUnassigned || !isAssigned,
      },
    });
  }

  hasSelectedFilterChanged = (prevState) => {
    const { selectedFilter } = this.state;
    const prev = prevState?.selectedFilter || {};
    return (
      selectedFilter.isAssigned !== prev.isAssigned ||
      selectedFilter.isUnassigned !== prev.isUnassigned ||
      selectedFilter.excludeSwapPickups !== prev.excludeSwapPickups
    );
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.hasSelectedFilterChanged(prevState)) {
      const listForFilters = this.getSelectionListForCurrentFilters();
      this.setState({ pendingSelectionFromFilter: listForFilters }, () => {
        this.props.setSelectedPickups(listForFilters);
        this.filterPickupsList();
      });
      return;
    }

    // Parent sent new list (e.g. refresh or step change): clear pending and re-apply filters.
    if (prevProps.showPickupsList !== this.props.showPickupsList) {
      this.setState({ pendingSelectionFromFilter: null });
      this.filterPickupsList();
    } else if (prevProps.selectedPickupsList !== this.props.selectedPickupsList) {
      if (this.state.pendingSelectionFromFilter) {
        this.setState({ pendingSelectionFromFilter: null });
      }
      this.handlePickupSelectionChange(prevProps.selectedPickupsList);
    }
  }

  componentWillUnmount() {
    this.props.glEventHub.off(
      "pickupSelectedOnMapPopup",
      this.onPickupSelectedOnMap
    );
  }

  handlePickupSelectionChange = (prevSelectedPickupsList) => {
    if (
      JSON.stringify(prevSelectedPickupsList) !== JSON.stringify(this.props.selectedPickupsList)
    ) {
      const ids = (this.props.selectedPickupsList || []).map((pickup) => pickup.pickupId);
      this.selectPickups(ids);
    }
  };

  getSelectedPickupIdList = () => {
    return this.props.selectedPickupsList.map(pickup=>pickup.pickupId.toLowerCase())
  }

  getDefaultUnassignedValue = () => {
    const { pickupListForOptimization } = this.props.DispatchStore;
    const selectedPickupIdList = this.getSelectedPickupIdList();
    return isPickupSelectedInGrid(selectedPickupIdList)
      ? isUnassignedPickupSelected(
        pickupListForOptimization,
        selectedPickupIdList
      )
      : true;
  };

  handleFilterChange = (event) => {
    event.persist();
    this.setState((prevState) => ({
      ...prevState,
      selectedFilter: {
        ...prevState.selectedFilter,
        [event.target.name]: event.target.checked,
      },
    }));
  };

  filterPickupsList = (afterUpdateCallback) => {
    const pickupsList = this.props.DispatchStore.pickupListForOptimization;
    const filters = this.state.filters.filter(
      (option) => this.state.selectedFilter[option.name]
    );
    let showPickupsList = filters.length
      ? pickupsList.filter((pickup) =>
          isPickupFiltered({
            pickup,
            filters,
            terminalOffset: this.props.DispatchStore.terminalOffset,
          })
        )
      : [];
      
    if (this.state.selectedFilter.excludeSwapPickups) {
      showPickupsList = showPickupsList.filter(
        (pickup) => !pickup.isSwapPickup
      );
    }
    this.setState({ showPickupsList }, afterUpdateCallback);
  };

  /** Returns the list of pickups that should be selected for the current Assigned/Unassigned filters (same as IBBills data-driven selection). */
  getSelectionListForCurrentFilters = () => {
    const pickupsList = this.props.DispatchStore.pickupListForOptimization;
    const { selectedPickupsList } = this.props;
    const selected = selectedPickupsList || [];
    let selectedAssignedPickups = [];
    let selectedUnassignedPickups = [];
    const activeFilters = this.state.filters
      .filter((option) => this.state.selectedFilter[option.name])
      .map((option) => option.label);
    if (activeFilters.includes(PICKUP_OPTIMYZER_FILTERS.assigned)) {
      if (isPartialAssignedPickupsSelected(selected, pickupsList)) {
        selectedAssignedPickups = getAllAssignedPickups(selected);
      } else {
        selectedAssignedPickups = getAllAssignedPickups(pickupsList);
      }
    }
    if (activeFilters.includes(PICKUP_OPTIMYZER_FILTERS.unassigned)) {
      if (isPartialUnassignedPickupsSelected(selected, pickupsList)) {
        selectedUnassignedPickups = getAllUnassignedPickups(selected);
      } else {
        selectedUnassignedPickups = getAllUnassignedPickups(pickupsList);
      }
    }
    return [...selectedAssignedPickups, ...selectedUnassignedPickups];
  };

  getPickupIdsForGridSelection = () => {
    return (
      this.state.pendingSelectionFromFilter ||
      this.props.selectedPickupsList ||
      []
    ).map((p) => p.pickupId);
  };

  syncGridSelectionWithCurrentState = () => {
    this.selectPickups(this.getPickupIdsForGridSelection());
  };

  onCellValueChanged = (rowValue) => {
    const field = rowValue.colDef.field;
    const value = rowValue.value;
    if (this.isUpdateValid({ field, value, currentCell: rowValue })) {
      let data;
      this.props.showPickupUpdateInProgress();
      if (field === "pse") {
        data = {
          terminalCode: this.props.userData.userProfile.terminal.code,
          pickups: [
            {
              pickupId: rowValue.data.pickupId,
              pickupReadyTime: rowValue.data.pickupReadyTime,
              pickupCloseTime: rowValue.data.pickupCloseTime,
              pse: rowValue.newValue,
            },
          ],
        };
      } else {
        const [startTime, endTime] =
          rowValue.newValue && rowValue.newValue.split("-");
        data = {
          terminalCode: this.props.userData.userProfile.terminal.code,
          pickups: [
            {
              pickupId: rowValue.data.pickupId,
              pickupReadyTime: startTime,
              pickupCloseTime: endTime,
              pse: rowValue.data.pse,
            },
          ],
        };
      }
      postData("UpdatePickupData", data).then(
        (response) => {
          if (response && response.status >= 200 && response.status < 400) {
            this.props.glEventHub.emit(
              "DispatchEvents",
              "RefreshDispatchStore",
              []
            );
            this.props.enqueueSnackbar(
              MESSAGES.OPTIMIZATION_PICKUP.UPDATE_SUCCESS,
              { variant: "success" }
            );
            this.props.hidePickupUpdateInProgress();
          } else {
            this.handleInvalidUpdate({field, rowValue, statusCode: response.status});
          }
        },
        () => {
          this.handleInvalidUpdate({field, rowValue});
        }
      );
    } else {
      this.restoreCellValue(field, rowValue);
    }
  };
  isUpdateValid = ({ field, value, currentCell }) => {
    let isUpdateValid = true;
    switch (field) {
      case "pse":
        if (value === "") {
          this.props.enqueueSnackbar(MESSAGES.OPTIMIZATION_PICKUP.PSE_EMPTY, {
            variant: "error",
          });
          isUpdateValid = false;
        } else if (value === 0) {
          this.props.enqueueSnackbar(
            MESSAGES.OPTIMIZATION_PICKUP.PSE_ZEROVALUE,
            {
              variant: "error",
            }
          );
          isUpdateValid = false;
        } else if (
          currentCell &&
          Number(currentCell.oldValue) === Number(value)
        ) {
          this.props.enqueueSnackbar(MESSAGES.COMMON.NO_CHANGES_TO_SAVE, {
            variant: "info",
          });
          isUpdateValid = false;
        }
        break;
      case "pickupWindow":
        if (!value) {
          this.props.enqueueSnackbar(
            MESSAGES.OPTIMIZATION_PICKUP.PICKUPWINDOW_EMPTY,
            { variant: "error" }
          );
          isUpdateValid = false;
        } else if (
          (value &&
            (value.includes(appConstants.INVALID_TIME_RANGE) || value.includes("undefined"))) ||
          value.split("-").length != 2
        ) {
          this.props.enqueueSnackbar(
            MESSAGES.OPTIMIZATION_PICKUP.PICKUPWINDOW_INVALID,
            { variant: "error" }
          );
          isUpdateValid = false;
        } else if (currentCell && currentCell.oldValue === value) {
          this.props.enqueueSnackbar(MESSAGES.COMMON.NO_CHANGES_TO_SAVE, {
            variant: "info",
          });
          isUpdateValid = false;
        }
        break;
    }
    return isUpdateValid;
  };
  restoreCellValue = (field, rowValue) => {
    if (field === "pickupWindow") {
      const [startTime, endTime] =
        rowValue.oldValue && rowValue.oldValue.split("-");
      rowValue.data["pickupReadyTime"] = startTime;
      rowValue.data["pickupCloseTime"] = endTime;
      this.gridApi.refreshCells({ columns: [field], force: true });
    } else {
      rowValue.data[field] = rowValue.oldValue;
      this.gridApi.refreshCells({ columns: [field], force: true });
    }
  };

  handleInvalidUpdate = ({field, rowValue, statusCode}) => {
    this.restoreCellValue(field, rowValue);
    this.props.hidePickupUpdateInProgress();
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar(
      MESSAGES.OPTIMIZATION_PICKUP.PICKUP_UPDATE_FAILURE,
      { variant: "error" }
    );
  };

  batchSyncGridSelection = (shouldSelectNode) => {
    if (!this.gridApi?.forEachNode) {
      return null;
    }
    const toSelect = [];
    const toDeselect = [];
    let firstMatchingRowIndex = null;
    this.gridApi.forEachNode((node) => {
      const shouldSelect = shouldSelectNode(node);
      if (shouldSelect && firstMatchingRowIndex === null) {
        firstMatchingRowIndex = node.rowIndex;
      }
      if (shouldSelect && !node.isSelected()) {
        toSelect.push(node);
      } else if (!shouldSelect && node.isSelected()) {
        toDeselect.push(node);
      }
    });
    if (toDeselect.length) {
      this.gridApi.setNodesSelected({
        nodes: toDeselect,
        newValue: false,
        source: "api",
      });
    }
    if (toSelect.length) {
      this.gridApi.setNodesSelected({
        nodes: toSelect,
        newValue: true,
        source: "api",
      });
    }
    return firstMatchingRowIndex;
  };

  selectPickups = (pickupIdList = []) => {
    const normalizedPickupIds = new Set(
      pickupIdList.map((pickupId) => String(pickupId).toLowerCase())
    );
    this.batchSyncGridSelection((node) =>
      normalizedPickupIds.has(String(node.data.pickupId).toLowerCase())
    );
  };

  constructStatusBar = (filteredData) => {
    return [
      {
        label: filteredData.length + " Total Pickups",
        filterCondition: "",
      },
      {
        label: this.props.selectedPickupsList.length + " Selected Pickups",
        filterCondition: "",
      },
    ];
  };

  onSelectionChanged = () => {
    if (this.gridApi) {
      const selectedPickupsList = this.gridApi.getSelectedRows()
        .map((pickup) => {
          return {
            pickupId: pickup.pickupId,
            position: {
              lat: pickup.shipperLatitude,
              lng: pickup.shipperLongitude,
            },
            ...pickup,
          };
        });
      this.props.glEventHub.emit(
        "pickupSelectedOnGridPopup",
        selectedPickupsList
      );
      this.props.setSelectedPickups(selectedPickupsList);
    }
  };

  onPickupSelectedOnMap = (pickups) => {
    const matchSet = new Set(
      (pickups || []).map((pickup) => pickup.pickupId?.toLowerCase())
    );
    const firstMatchRowIndex = this.batchSyncGridSelection((node) =>
      matchSet.has(node.data.pickupId?.toLowerCase())
    );
    if (firstMatchRowIndex != null) {
      this.gridApi.ensureIndexVisible?.(firstMatchRowIndex);
    }
  };

  onRowDataUpdated = () => {
    this.syncGridSelectionWithCurrentState();
  };

  getPickupsListWithSelection = () => {
    const effectiveSelectedIds = (
      this.state.pendingSelectionFromFilter ||
      this.props.selectedPickupsList ||
      []
    ).map((p) => String(p.pickupId).toLowerCase());
    const showPickupsList = this.state.showPickupsList.length > 0 ? this.state.showPickupsList : [];
    return showPickupsList.map((p) => ({
      ...p,
      isSelected: effectiveSelectedIds.includes(String(p.pickupId).toLowerCase()),
    }));
  };

  render() {
    const { isAssigned, isUnassigned, excludeSwapPickups } = this.state.selectedFilter;
    const { classes } = this.props;
    const pickupsList = this.props.DispatchStore.pickupListForOptimization;
    const pickupsListWithSelection = this.getPickupsListWithSelection();
    return (
      <Grid container className={classes.container}>
        <Grid item xs={4} className={classes.topGridContainer}>
          <PickupCheckbox
            isPartialUnassignedPickupsSelected={isPartialUnassignedPickupsSelected(
              this.props.selectedPickupsList,
              pickupsList
            )}
            isPartialAssignedPickupsSelected={isPartialAssignedPickupsSelected(
              this.props.selectedPickupsList,
              pickupsList
            )}
            isAssigned={isAssigned}
            isUnassigned={isUnassigned}
            excludeSwapPickups={excludeSwapPickups}
            handleFilterChange={this.handleFilterChange}
          />
          <div className={classes.bottomGridContainer}>
            <div className={classes.headerRow}>
              <span className={classes.headerStyle}>Pickups for Optimization</span>
              <RefreshStatus
                onRefresh={this.props.onRefreshPickupsData}
                defaultTooltip="Refresh pickups"
                refreshingTooltip="Refreshing…"
              />
            </div>
            <div className={classes.dataGrid}>
              <DataGrid
                defaultColDef={this.gridConfig.config.defaultColDef}
                columnDefs={this.gridConfig.config.columns}
                columnTypes={this.gridConfig.config.columnTypes}
                config={this.gridConfig.config}
                context={this.gridContext}
                onGridReady={this.onGridReady}
                rowSelection={this.gridConfig.config.rowSelection}
                rowDeselection={this.gridConfig.config.rowDeselection}
                rowData={this.state.showPickupsList}
                getRowId={(params) => params.data?.pickupId ?? String(params.rowIndex)}
                onSelectionChanged={this.onSelectionChanged}
                onRowDataUpdated={this.onRowDataUpdated}
                rowHeight={65}
                suppressRowClickSelection
                onCellValueChanged={this.onCellValueChanged}
                panelLabel={(filteredData) => (
                  <StatusBar data={this.constructStatusBar(filteredData)} />
                )}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={8} className={classes.mapContainer}>
          <PickupMap
            pickupList={pickupsListWithSelection}
            glEventHub={this.props.glEventHub}
            userData={this.props.userData}
          />
        </Grid>
      </Grid>
    );
  }
}

export default compose(
  withStyles(styles),
  withSnackbar,
  withFeatureFlags
)(CorrectPickupInfo);
